import { X } from '@ui/components/icons';
import { WalletAddress } from '@ui/components/WalletAddress';
import { useMutationToast } from '@ui/hooks/useMutationToast';
import { Button } from '@ui/lib/park/ui/button';
import { Dialog } from '@ui/lib/park/ui/dialog';
import { Icon } from '@ui/lib/park/ui/icon';
import { Input } from '@ui/lib/park/ui/input';
import { Text } from '@ui/lib/park/ui/text';
import { useJoinTradingCompetitionMutation } from '@ui/services/backend/hooks/useTradingCompetitions/useJoinTradingCompetitionMutation';
import { shortenAddress } from '@ui/utils/formatAddress';
import { useTranslation } from 'next-i18next';
import { useEffect, useRef, useState } from 'react';
import { Box, Flex, HStack, Stack } from 'styled-system/jsx';
import { zeroAddress } from 'viem';
import { useAccount } from 'wagmi';

import type { Competition } from './types';

const extractJoinCompetitionErrorMessage = async (err: any) => {
  // Generated backend client throws ResponseError with a Response attached
  if (err?.name === 'ResponseError' && err?.response) {
    try {
      const res: Response = err.response;
      const contentType = res.headers?.get?.('content-type') || '';
      if (contentType.includes('application/json')) {
        const json = await res.clone().json().catch(() => undefined);
        if (typeof json?.error === 'string') return json.error;
        if (typeof json?.message === 'string') return json.message;
      }

      const text = await res.clone().text().catch(() => '');
      if (text) {
        try {
          const parsed = JSON.parse(text);
          if (typeof parsed?.error === 'string') return parsed.error;
          if (typeof parsed?.message === 'string') return parsed.message;
        } catch {
          // ignore JSON parse failures
        }
        
return text;
      }
    } catch {
      // ignore parsing errors
    }
  }

  // Common non-ResponseError shapes
  if (typeof err?.error === 'string') return err.error;
  if (typeof err?.message === 'string') return err.message;
  
return undefined;
};

export const CompetitionJoinModal = ({
  open,
  onOpenChange,
  competition,
  onJoined,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  competition?: Competition | null;
  onJoined?: (competitionId: number) => void;
}) => {
  const { t } = useTranslation();
  const { address, isConnected } = useAccount();
  const toast = useMutationToast();
  const joinMutation = useJoinTradingCompetitionMutation();

  const [inviteCode, setInviteCode] = useState('');
  const [error, setError] = useState<string | undefined>(undefined);

  // react-query's mutation object may not be referentially stable across renders,
  // so keep reset in a ref to avoid effect dependency loops.
  const resetRef = useRef(joinMutation.reset);
  useEffect(() => {
    resetRef.current = joinMutation.reset;
  }, [joinMutation.reset]);

  useEffect(() => {
    if (!open) {
      setInviteCode('');
      setError(undefined);
      resetRef.current();
    }
  }, [open]);

  const onSubmit = async () => {
    if (!competition) return;
    if (!isConnected || !address) return;

    const trimmed = inviteCode.trim();
    if (!trimmed) {
      setError(t('competition.inviteCodeRequired', 'Invite code is required.'));
      
return;
    }

    try {
      setError(undefined);
      toast.onMutate({
        title: 'Joining',
        description: 'Submitting invite code...',
      });
      await joinMutation.mutateAsync({
        trader: address,
        inviteCode: trimmed,
      });
      toast.onSuccess({
        title: 'Joined',
        description: 'You have joined successfully!',
      });
      onJoined?.(competition.id);
      onOpenChange(false);
    } catch (err) {
      const msg =
        (await extractJoinCompetitionErrorMessage(err)) ||
        t('common.Something went wrong');
      setError(msg);
      toast.onError({ errorMsg: msg });
    } finally {
      toast.onSettled();
    }
  };

  return (
    <Dialog.Root
      lazyMount
      unmountOnExit
      open={open}
      onOpenChange={(e) => onOpenChange(!!e.open)}
    >
      <Dialog.Backdrop />
      <Dialog.Positioner>
        <Dialog.Content
          p={4}
          bg="v2.bg.panel"
          border="1px solid"
          borderColor="v2.border.default"
          borderRadius="4px"
          w={{ base: '92vw', tablet: '400px' }}
        >
          <Stack gap={4}>
            <HStack justifyContent="space-between">
              <Dialog.Title p={0}>
                <HStack gap={2} flexWrap="wrap">
                  <Text
                    fontFamily="Inter"
                    fontSize="15px"
                    fontWeight={600}
                    color="v2.text.primary"
                  >
                    Join in
                  </Text>
                  {competition?.title ? (
                    <Text textStyle="v2.label" color="v2.text.muted">
                      {competition.title}
                    </Text>
                  ) : null}
                </HStack>
              </Dialog.Title>
              <Dialog.CloseTrigger
                boxShadow="none"
                bg="none"
                color="v2.text.muted"
                _hover={{ color: 'v2.text.primary' }}
              >
                <Icon w="18px" h="18px">
                  <X />
                </Icon>
              </Dialog.CloseTrigger>
            </HStack>

            <Stack gap={3}>
              <HStack
                justify="space-between"
                bg="v2.bg.surface1"
                border="1px solid"
                borderColor="v2.border.default"
                borderRadius="2px"
                px={3}
                py="10px"
              >
                <Text textStyle="v2.label" color="v2.text.muted">
                  Account
                </Text>
                <Text textStyle="numbers.small" color="v2.text.primary">
                  {shortenAddress({ address: address || zeroAddress })}
                </Text>
              </HStack>

              <Stack gap={2}>
                <Text textStyle="v2.label" color="v2.text.muted">
                  Input a Participation Code to join
                </Text>
                <Input
                  placeholder="Input here"
                  bg="v2.bg.surface1"
                  border="1px solid"
                  borderColor="v2.border.default"
                  borderRadius="2px"
                  p={3}
                  color="v2.text.primary"
                  _focus={{ borderColor: 'v2.border.focus' }}
                  value={inviteCode}
                  onChange={(e) => setInviteCode(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') onSubmit();
                  }}
                  disabled={!isConnected || joinMutation.isPending}
                />
                {!!error && (
                  <Text textStyle="v2.meta" color="v2.short.default">
                    {error}
                  </Text>
                )}
              </Stack>

              <Button
                w="full"
                h="40px"
                variant="primary"
                loading={joinMutation.isPending}
                disabled={!isConnected}
                onClick={onSubmit}
              >
                Join
              </Button>
            </Stack>
          </Stack>
        </Dialog.Content>
      </Dialog.Positioner>
    </Dialog.Root>
  );
};
